import { useRef, useState } from 'react';
import SectionCard from '../../components/SectionCard.jsx';
import Dialog, { DialogError } from '../../components/Dialog.jsx';
import StatusWord from '../../components/StatusWord.jsx';
import { api } from '../../lib/api.js';
import { dateOnlyWords, personName, plural } from '../../lib/access-review-format.js';
import { NOTE_MAX, validateNote } from '../../../../shared/access-reviews.mjs';

export const NOTES_ID = 'access-reviews-notes';

const kindWord = (k) => (k === 'eligible' ? 'Eligible' : 'Active');
const assignmentLabel = (a) => `${personName(a.holder, 'Holder')}${a.holder?.upn && a.holder.upn !== a.holder.displayName ? ` (${a.holder.upn})` : ''}, ${a.role}, ${a.scopeLabel}, ${kindWord(a.kind).toLowerCase()}`;

/**
 * Role assignment notes (Hugo Andrade, 2026-10-26): why someone holds a role, an optional review-by date and an optional
 * owner ("Camila is Global Reader temporarily for the onGuard apps project; review when the project ends"). The customer's
 * admin only (the API sends `notes: null` to everyone else). Notes past their date are marked and listed first.
 */
export default function NotesCard({ notes, assignments, version, customerId, canEdit, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [delState, setDelState] = useState({ busy: false, error: null });
  const addRef = useRef(null);
  const sorted = [...notes].sort((a, b) => (b.due === true) - (a.due === true) || String(a.reviewBy ?? '9999').localeCompare(String(b.reviewBy ?? '9999')));
  const due = notes.filter((n) => n.due).length;

  const remove = async () => {
    setDelState({ busy: true, error: null });
    try {
      await api.deleteAccessReviewNote(customerId, deleting.noteId, version);
      setDeleting(null);
      setDelState({ busy: false, error: null });
      onChanged();
    } catch (e) {
      setDelState({ busy: false, error: noteError(e) });
    }
  };

  return (
    <SectionCard
      id={NOTES_ID} title="Notes" testId="access-reviews-notes"
      description={due > 0 ? `${plural(due, 'note')} past ${due === 1 ? 'its' : 'their'} review-by date` : 'Why someone holds a role, and when to look again. Reviewers see the note on the item.'}
      action={canEdit && assignments.length > 0 ? <button ref={addRef} type="button" className="btn-secondary min-h-[36px]" onClick={() => setEditing({})} data-testid="access-reviews-add-note">Add note</button> : null}
    >
      {sorted.length === 0 ? (
        <p className="text-sm text-ipc-muted" data-testid="access-reviews-notes-empty">No notes yet. Add one to explain an assignment, for example a temporary role for a project.</p>
      ) : (
        <ul className="divide-y divide-ipc-border">
          {sorted.map((n) => (
            <li key={n.noteId} className="flex flex-col gap-2 py-3 first:pt-0 sm:flex-row sm:items-start" data-note={n.noteId} data-due={n.due ? 'true' : 'false'}>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-ipc-ink">
                  <span className="font-semibold">{personName(n.holder, 'Holder')}</span>
                  <span className="text-ipc-muted">{`, ${n.role}, ${n.scopeLabel}`}</span>
                </p>
                <p className="mt-1 break-words text-sm text-ipc-ink">{n.text}</p>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-ipc-muted">
                  {n.reviewBy ? (n.due
                    ? <StatusWord tone="warn" testId="access-reviews-note-due">Review date passed: {dateOnlyWords(n.reviewBy)}</StatusWord>
                    : <span>Review by {dateOnlyWords(n.reviewBy)}</span>) : <span>No review date</span>}
                  {n.owner ? <span>Owner: {personName(n.owner)}</span> : null}
                </div>
              </div>
              {canEdit ? (
                <div className="flex shrink-0 gap-3 text-[13.5px]">
                  <button type="button" className="tap font-semibold text-ipc-light hover:underline" onClick={() => setEditing(n)}>Edit</button>
                  <button type="button" className="tap font-semibold text-ipc-light hover:underline" onClick={() => { setDelState({ busy: false, error: null }); setDeleting(n); }}>Delete</button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {editing ? (
        <NoteDialog
          note={editing.noteId ? editing : null} assignments={assignments} version={version} customerId={customerId} returnFocusRef={addRef}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }}
        />
      ) : null}
      <Dialog
        open={Boolean(deleting)} onClose={() => setDeleting(null)} title="Delete this note?"
        footer={(
          <>
            <button type="button" className="btn-secondary" onClick={() => setDeleting(null)}>Keep it</button>
            <button type="button" className="btn-primary" disabled={delState.busy} onClick={remove} data-testid="access-reviews-note-delete-confirm">{delState.busy ? 'Deleting' : 'Delete note'}</button>
          </>
        )}
      >
        <p>Reviewers no longer see it on the item. Notes already in a closed campaign's evidence stay there.</p>
        <DialogError message={delState.error} />
      </Dialog>
    </SectionCard>
  );
}

function noteError(e) {
  if (e?.status === 409) return 'Someone changed the notes a moment ago. Close this, reload the page and try again.';
  return e?.body?.message ?? e?.message ?? 'The note was not saved. Try again.';
}

/** Add or edit a note: the assignment (from the API's `assignments`), the text, an optional review-by date and owner. */
export function NoteDialog({ note, assignments, version, customerId, onClose, onSaved, returnFocusRef }) {
  const [form, setForm] = useState(() => ({
    assignmentKey: note?.assignmentKey ?? '',
    text: note?.text ?? '',
    reviewBy: note?.reviewBy ?? '',
    owner: note?.owner?.upn ?? '',
  }));
  const [state, setState] = useState({ busy: false, error: null });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const known = new Set(assignments.map((a) => a.assignmentKey));

  const save = async () => {
    const body = { assignmentKey: form.assignmentKey, text: form.text, reviewBy: form.reviewBy || null, owner: form.owner.trim() || null, version };
    const v = validateNote(body);
    if (!v.ok) { setState({ busy: false, error: v.error }); return; }
    setState({ busy: true, error: null });
    try {
      if (note) await api.updateAccessReviewNote(customerId, note.noteId, body);
      else await api.createAccessReviewNote(customerId, body);
      setState({ busy: false, error: null });
      onSaved();
    } catch (e) {
      setState({ busy: false, error: noteError(e) });
    }
  };

  return (
    <Dialog
      open onClose={onClose} returnFocusRef={returnFocusRef} wide
      title={note ? 'Edit note' : 'Add a note to a role assignment'}
      description="Reviewers see the note on the item. When the review-by date passes, this page shows a notice."
      footer={(
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" disabled={state.busy} onClick={save} data-testid="access-reviews-note-save">{state.busy ? 'Saving' : 'Save note'}</button>
        </>
      )}
    >
      <label className="block">
        <span className="label">Role assignment</span>
        <select className="input" value={form.assignmentKey} onChange={set('assignmentKey')} disabled={Boolean(note)} data-testid="access-reviews-note-assignment">
          <option value="">Pick a person and role</option>
          {note && !known.has(note.assignmentKey) ? <option value={note.assignmentKey}>{`${personName(note.holder, 'Holder')}, ${note.role}, ${note.scopeLabel}`}</option> : null}
          {assignments.map((a) => <option key={a.assignmentKey} value={a.assignmentKey}>{assignmentLabel(a)}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="label">Note</span>
        <textarea
          className="input min-h-[96px]" maxLength={NOTE_MAX} value={form.text} onChange={set('text')} data-testid="access-reviews-note-text"
          placeholder="Camila is Global Reader temporarily for the onGuard apps project; review when the project ends."
        />
      </label>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="label">Review by (optional)</span>
          <input type="date" className="input" value={form.reviewBy} onChange={set('reviewBy')} data-testid="access-reviews-note-review-by" />
        </label>
        <label className="block">
          <span className="label">Owner (optional)</span>
          <input type="text" className="input" value={form.owner} onChange={set('owner')} placeholder="Sign-in name or email" autoComplete="off" data-testid="access-reviews-note-owner" />
        </label>
      </div>
      <DialogError message={state.error} />
    </Dialog>
  );
}

